import React, { useMemo, useState } from 'react';
import { MobileLayout } from '../../components/MobileLayout';
import { Tabs } from '../../components/ui/Tabs';
import { Card, CardHeader, CardContent, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MapPin, FileText, Clock, CheckCircle2, XCircle, FilePlus, Send, X, Inbox, User, Home } from 'lucide-react';
import { useScreenInit } from '../../useScreenInit.js';
import { useContracts } from '../../context/ContractsContext';
import { usePlatform } from '../../context/PlatformContext';
type ProposalStatus = 'inviata' | 'accettata' | 'rifiutata' | 'formalizzata';
interface Proposal {
  id: string;
  cliente: string;
  appartamento: string;
  zone: string;
  contract: string;
  unitPrice: number;
  interventi: number;
  date: string;
  status: ProposalStatus;
}
const INITIAL_PROPOSALS: Proposal[] = [{
  id: '1',
  cliente: 'Marco Rossi',
  appartamento: 'Loft Navigli',
  zone: 'Navigli',
  contract: 'Contratto 10',
  unitPrice: 41,
  interventi: 10,
  date: '22 Gen',
  status: 'inviata'
}, {
  id: '2',
  cliente: 'Laura Bianchi',
  appartamento: 'Studio Isola',
  zone: 'Isola',
  contract: 'Contratto 30',
  unitPrice: 36,
  interventi: 30,
  date: '20 Gen',
  status: 'accettata'
}, {
  id: '3',
  cliente: 'Elena Colombo',
  appartamento: 'Bilo Porta Romana',
  zone: 'Porta Romana',
  contract: 'Singolo',
  unitPrice: 50,
  interventi: 1,
  date: '19 Gen',
  status: 'rifiutata'
}, {
  id: '4',
  cliente: 'Giuseppe Verdi',
  appartamento: 'Trilo Centro',
  zone: 'Centro',
  contract: 'Contratto 10',
  unitPrice: 44,
  interventi: 10,
  date: '18 Gen',
  status: 'inviata'
}, {
  id: '5',
  cliente: 'Sophie Laurent',
  appartamento: 'Attico Tortona',
  zone: 'Tortona',
  contract: 'Contratto 15',
  unitPrice: 45,
  interventi: 15,
  date: '16 Gen',
  status: 'accettata'
}];
const propStatus: Record<ProposalStatus, {
  label: string;
  cls: string;
}> = {
  inviata: {
    label: 'In attesa',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  accettata: {
    label: 'Accettata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  rifiutata: {
    label: 'Rifiutata',
    cls: 'bg-red-50 text-red-600 border-red-200'
  },
  formalizzata: {
    label: 'Formalizzata',
    cls: 'bg-navy/5 text-navy border-navy/20'
  }
};
type ProposteTab = 'da_formalizzare' | 'inviate' | 'rifiutate' | 'contratti';
function CountBadge({
  n,
  active
}: {
  n: number;
  active: boolean;
}) {
  if (n === 0) return null;
  return <span className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground'}`} data-id="element-2301">
      {n}
    </span>;
}
export function CmPropostePage() {
  const screenInit = useScreenInit() as {
    tab?: string;
  };
  const {
    contracts,
    addContract
  } = useContracts();
  const {
    generateContractJobs
  } = usePlatform();
  const [proposals, setProposals] = useState<Proposal[]>(INITIAL_PROPOSALS);
  const [toFormalize, setToFormalize] = useState<Proposal | null>(null);
  const [justFormalized, setJustFormalized] = useState<string | null>(null);
  const daFormalizzare = proposals.filter(p => p.status === 'accettata');
  const inviate = proposals.filter(p => p.status === 'inviata');
  const rifiutate = proposals.filter(p => p.status === 'rifiutata');
  const initialTab = useMemo<ProposteTab>(() => {
    const fromInit = screenInit?.tab as ProposteTab | undefined;
    if (fromInit === 'da_formalizzare' || fromInit === 'inviate' || fromInit === 'rifiutate' || fromInit === 'contratti') {
      return fromInit;
    }
    return daFormalizzare.length > 0 ? 'da_formalizzare' : 'inviate';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tab, setTab] = useState<ProposteTab>(initialTab);
  const confirmFormalize = () => {
    if (!toFormalize) return;
    addContract({
      zone: toFormalize.zone,
      size: toFormalize.interventi,
      unitPrice: toFormalize.unitPrice,
      done: 0
    });
    // Generate the planned cleaning jobs for this contract so they appear in
    // Incarichi as "Da assegnare".
    generateContractJobs({
      apartmentName: toFormalize.appartamento,
      zone: toFormalize.zone,
      count: toFormalize.interventi,
      type: 'Pulizia turnover',
      earning: toFormalize.unitPrice
    });
    setProposals(prev => prev.map(p => p.id === toFormalize.id ? {
      ...p,
      status: 'formalizzata'
    } : p));
    setJustFormalized(toFormalize.zone);
    setToFormalize(null);
  };
  const renderProposalMeta = (p: Proposal) => <div className="space-y-2" data-id="element-2302">
      {/* Cliente & appartamento */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs" data-id="element-2303">
        <span className="flex items-center gap-1.5 text-foreground font-medium" data-id="element-2304">
          <User size={12} className="text-coral" data-id="element-2305" />
          {p.cliente}
        </span>
        <span className="flex items-center gap-1.5 text-foreground font-medium" data-id="element-2306">
          <Home size={12} className="text-coral" data-id="element-2307" />
          {p.appartamento}
        </span>
      </div>
      {/* Commercials */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap" data-id="element-2308">
        <span className="flex items-center gap-1" data-id="element-2309">
          <MapPin size={12} data-id="element-2310" />
          {p.zone}
        </span>
        <span data-id="element-2311">€{p.unitPrice}/intervento</span>
        <span data-id="element-2312">· {p.interventi} interventi</span>
        <span className="font-medium text-foreground" data-id="element-2313">
          €{p.unitPrice * p.interventi} totale
        </span>
        <span className="flex items-center gap-1" data-id="element-2314">
          <Clock size={12} data-id="element-2315" />
          {p.date}
        </span>
      </div>
    </div>;
  return <MobileLayout data-id="element-2316">
      <div className="mb-5" data-id="element-2317">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2318">Proposte</h1>
      </div>

      {/* Success banner */}
      {justFormalized && <div className="mb-4 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-id="element-2319">
          <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-2320" />
          <div className="flex-1 min-w-0" data-id="element-2321">
            <p className="text-sm font-medium text-emerald-800" data-id="element-2322">
              Contratto formalizzato
            </p>
            <p className="text-xs text-emerald-700" data-id="element-2323">
              Il contratto per la zona {justFormalized} è ora attivo. Lo trovi
              nel tab “Contratti attivi”.
            </p>
          </div>
          <button onClick={() => setJustFormalized(null)} aria-label="Chiudi" className="p-1 text-emerald-600 hover:text-emerald-800" data-id="element-2324">
            <X size={16} data-id="element-2325" />
          </button>
        </div>}

      <Tabs className="mb-6" scrollable value={tab} onValueChange={id => setTab(id as ProposteTab)} items={[{
      id: 'da_formalizzare',
      label: 'Da formalizzare',
      badge: <CountBadge n={daFormalizzare.length} active={tab === 'da_formalizzare'} data-id="element-2327" />
    }, {
      id: 'inviate',
      label: 'Inviate',
      badge: <CountBadge n={inviate.length} active={tab === 'inviate'} data-id="element-2328" />
    }, {
      id: 'rifiutate',
      label: 'Rifiutate',
      badge: <CountBadge n={rifiutate.length} active={tab === 'rifiutate'} data-id="element-2329" />
    }, {
      id: 'contratti',
      label: 'Contratti attivi',
      badge: <CountBadge n={contracts.length} active={tab === 'contratti'} data-id="element-2330" />
    }]} data-id="element-2326" />

      {/* DA FORMALIZZARE */}
      {tab === 'da_formalizzare' && (daFormalizzare.length > 0 ? <div className="space-y-3" data-id="element-2331">
            {daFormalizzare.map(p => <Card key={p.id} data-id="element-2332">
                <CardHeader icon={<CheckCircle2 size={15} className="text-emerald-600" data-id="element-2334" />} title={`${p.contract} · ${p.zone}`} action={<span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${propStatus[p.status].cls}`} data-id="element-2335">
                      {propStatus[p.status].label}
                    </span>} data-id="element-2333" />
                <CardContent data-id="element-2336">{renderProposalMeta(p)}</CardContent>
                <CardFooter data-id="element-2337">
                  <span className="text-xs text-muted-foreground" data-id="element-2338">
                    Accettata dal cliente · in attesa di contratto
                  </span>
                  <Button size="sm" onClick={() => setToFormalize(p)} data-id="element-2339">
                    <FilePlus size={15} data-id="element-2340" />
                    Formalizza contratto
                  </Button>
                </CardFooter>
              </Card>)}
          </div> : <EmptyState icon={<FilePlus size={20} className="text-muted-foreground" data-id="element-2342" />} title="Nessuna proposta da formalizzare" subtitle="Le proposte accettate dai clienti compariranno qui pronte per la formalizzazione del contratto." data-id="element-2341" />)}

      {/* INVIATE */}
      {tab === 'inviate' && (inviate.length > 0 ? <div className="space-y-3" data-id="element-2343">
            {inviate.map(p => <Card key={p.id} data-id="element-2344">
                <CardHeader icon={<Send size={15} className="text-coral" data-id="element-2346" />} title={`${p.contract} · ${p.zone}`} action={<span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${propStatus[p.status].cls}`} data-id="element-2347">
                      {propStatus[p.status].label}
                    </span>} data-id="element-2345" />
                <CardContent data-id="element-2348">{renderProposalMeta(p)}</CardContent>
              </Card>)}
          </div> : <EmptyState icon={<Send size={20} className="text-muted-foreground" data-id="element-2350" />} title="Nessuna proposta in attesa" subtitle="Le proposte inviate ai clienti e ancora in attesa di risposta compariranno qui." data-id="element-2349" />)}

      {/* RIFIUTATE */}
      {tab === 'rifiutate' && (rifiutate.length > 0 ? <div className="space-y-3" data-id="element-2351">
            {rifiutate.map(p => <Card key={p.id} className="opacity-90" data-id="element-2352">
                <CardHeader icon={<XCircle size={15} className="text-red-500" data-id="element-2354" />} title={`${p.contract} · ${p.zone}`} action={<span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${propStatus[p.status].cls}`} data-id="element-2355">
                      {propStatus[p.status].label}
                    </span>} data-id="element-2353" />
                <CardContent data-id="element-2356">{renderProposalMeta(p)}</CardContent>
              </Card>)}
          </div> : <EmptyState icon={<XCircle size={20} className="text-muted-foreground" data-id="element-2358" />} title="Nessuna proposta rifiutata" subtitle="Le proposte non andate a buon fine compariranno qui." data-id="element-2357" />)}

      {/* CONTRATTI ATTIVI */}
      {tab === 'contratti' && (contracts.length > 0 ? <div className="space-y-3" data-id="element-2359">
            {contracts.map(c => {
        const pct = c.size > 0 ? Math.round(c.done / c.size * 100) : 0;
        return <Card key={c.id} data-id="element-2360">
                  <CardHeader title={`Contratto ${c.size} · ${c.zone}`} subtitle={`€${c.unitPrice}/intervento · €${c.unitPrice * c.size} totale`} action={<span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-2362">
                        Attivo
                      </span>} data-id="element-2361" />
                  <CardContent data-id="element-2363">
                    {c.fileName && <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 min-w-0" data-id="element-2364">
                        <FileText size={13} className="text-coral flex-shrink-0" data-id="element-2365" />
                        <span className="truncate" data-id="element-2366">{c.fileName}</span>
                      </div>}
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5" data-id="element-2367">
                      <span data-id="element-2368">Avanzamento</span>
                      <span className="font-medium text-coral" data-id="element-2369">
                        {c.done}/{c.size} interventi
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-id="element-2370">
                      <div className="bg-coral h-2 rounded-full transition-all" style={{
                width: `${pct}%`
              }} data-id="element-2371" />
                    </div>
                  </CardContent>
                </Card>;
      })}
          </div> : <EmptyState icon={<Inbox size={20} className="text-muted-foreground" data-id="element-2373" />} title="Nessun contratto attivo" subtitle="Formalizza una proposta accettata per generare il primo contratto." data-id="element-2372" />)}

      {/* Formalize confirmation modal */}
      {toFormalize && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setToFormalize(null)} data-id="element-2374">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Formalizza contratto" data-id="element-2375">
            <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-2376">
              <h2 className="text-lg font-bold text-navy" data-id="element-2377">
                Formalizza contratto
              </h2>
              <button onClick={() => setToFormalize(null)} aria-label="Chiudi" className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2378">
                <X size={20} className="text-gray-500" data-id="element-2379" />
              </button>
            </div>
            <div className="p-4 space-y-4" data-id="element-2380">
              <p className="text-sm text-muted-foreground" data-id="element-2381">
                Stai per trasformare la proposta accettata in un contratto
                attivo. Verifica le condizioni:
              </p>
              <div className="rounded-xl border border-border divide-y divide-border" data-id="element-2382">
                <Row label="Zona" value={toFormalize.zone} data-id="element-2383" />
                <Row label="Piano" value={toFormalize.contract} data-id="element-2384" />
                <Row label="Interventi" value={`${toFormalize.interventi}`} data-id="element-2385" />
                <Row label="Prezzo / intervento" value={`€${toFormalize.unitPrice}`} data-id="element-2386" />
                <Row label="Valore totale" value={`€${toFormalize.unitPrice * toFormalize.interventi}`} emphasize data-id="element-2387" />
              </div>
            </div>
            <div className="p-4 border-t border-border bg-gray-50 flex justify-end gap-3" data-id="element-2388">
              <Button variant="subtle" onClick={() => setToFormalize(null)} data-id="element-2389">
                Annulla
              </Button>
              <Button onClick={confirmFormalize} data-id="element-2390">
                <FilePlus size={16} data-id="element-2391" />
                Conferma e attiva
              </Button>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}
function Row({
  label,
  value,
  emphasize = false
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return <div className="flex items-center justify-between px-3 py-2.5" data-id="element-2392">
      <span className="text-xs text-muted-foreground" data-id="element-2393">{label}</span>
      <span className={`text-sm ${emphasize ? 'font-bold text-navy' : 'font-medium text-foreground'}`} data-id="element-2394">
        {value}
      </span>
    </div>;
}
function EmptyState({
  icon,
  title,
  subtitle
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-2395">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2396">
        {icon}
      </div>
      <p className="text-sm font-medium text-foreground" data-id="element-2397">{title}</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto" data-id="element-2398">
        {subtitle}
      </p>
    </div>;
}